import JSZip from 'jszip';

// Minimal .xlsx reader/writer built on the jszip the portal already bundles.
//
// Why not a library: SheetJS's npm builds are frozen at a version with open advisories, and ExcelJS needs
// webpack-5 node polyfills that CRA no longer ships. An .xlsx is just a zip of XML, and the subset we need
// — a header row plus text/number cells — is small enough to own outright. Reading uses DOMParser rather
// than regex so files re-saved by Excel, Numbers or Google Sheets parse correctly.
//
// Generic on purpose: any screen that needs a spreadsheet download or upload should use this rather than
// adding a second implementation. Payment Porter's rule sheets are defined in utils/ppRuleWorkbook.js.

// ─── Shared ──────────────────────────────────────────────────────────────────

const esc = (s) => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Control characters are illegal in XML 1.0 and make the whole workbook unopenable — drop them.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

// 0 → A, 25 → Z, 26 → AA
export const colName = (index) => {
    let n = index, name = '';
    do { name = String.fromCharCode(65 + (n % 26)) + name; n = Math.floor(n / 26) - 1; } while (n >= 0);
    return name;
};

const colIndex = (name) => {
    let n = 0;
    for (const ch of name) n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
};

// Excel limits sheet names to 31 chars and forbids : \ / ? * [ ]
export const safeSheetName = (name) => String(name).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);

// ─── Write ───────────────────────────────────────────────────────────────────

// Two cell formats: 0 = default, 1 = bold (the header row). Enough for a template that is meant to be
// filled in, not designed.
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

// A cell is written as a number only when it is a real JS number. Everything else goes out as an inline
// string, which keeps codes like "007" or "1-9" intact instead of letting Excel coerce them.
const cellXml = (value, ref, styleId) => {
    const s = styleId ? ` s="${styleId}"` : '';
    if (value == null || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}"${s}><v>${value}</v></c>`;
    return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(value)}</t></is></c>`;
};

const sheetXml = ({ columns = [], rows = [] }) => {
    const headers = columns.map(c => (typeof c === 'string' ? c : c.header));
    const widths = columns
        .map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${(typeof c === 'object' && c.width) || 20}" customWidth="1"/>`)
        .join('');
    const body = [headers, ...rows]
        .map((row, r) => {
            const cells = row.map((v, c) => cellXml(v, `${colName(c)}${r + 1}`, r === 0 ? 1 : 0)).join('');
            return `<row r="${r + 1}">${cells}</row>`;
        })
        .join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${widths ? `<cols>${widths}</cols>` : ''}
<sheetData>${body}</sheetData>
</worksheet>`;
};

/**
 * Build an .xlsx Blob.
 * @param {Array<{name: string, columns: Array<string|{header: string, width?: number}>, rows: Array<Array<string|number|null>>}>} sheets
 * @returns {Promise<Blob>}
 */
export const writeWorkbook = async (sheets) => {
    const zip = new JSZip();
    const names = sheets.map(s => safeSheetName(s.name));

    zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}
</Types>`);

    zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`);

    zip.file('xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${names.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>
</workbook>`);

    zip.file('xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}
<Relationship Id="rIdStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`);

    zip.file('xl/styles.xml', STYLES_XML);
    sheets.forEach((s, i) => zip.file(`xl/worksheets/sheet${i + 1}.xml`, sheetXml(s)));

    return zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        compression: 'DEFLATE',
    });
};

/** Trigger a browser download of a Blob under `filename`. */
export const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoking synchronously can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Read ────────────────────────────────────────────────────────────────────

const parseXml = (text) => {
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) throw new Error('The file is not a valid .xlsx workbook');
    return doc;
};

// An <si> may be a single <t> or a series of rich-text runs; concatenating every <t> gives the value in
// both cases. <rPh> holds phonetic hints (Japanese locales) and must be excluded or it doubles the text.
const siText = (si) => {
    Array.from(si.getElementsByTagName('rPh')).forEach(n => n.parentNode.removeChild(n));
    return Array.from(si.getElementsByTagName('t')).map(t => t.textContent).join('');
};

const cellValue = (c, shared) => {
    const type = c.getAttribute('t');
    if (type === 'inlineStr') {
        const is = c.getElementsByTagName('is')[0];
        return is ? siText(is) : '';
    }
    const v = c.getElementsByTagName('v')[0];
    if (!v) return '';
    if (type === 's') return shared[parseInt(v.textContent, 10)] ?? '';
    if (type === 'b') return v.textContent === '1' ? 'TRUE' : 'FALSE';
    // 'str' (formula result), 'n' and untyped all read as their literal text. Numbers stay strings so a
    // code typed as 007 is not silently renumbered; callers coerce where they mean a number.
    return v.textContent;
};

/**
 * Read an .xlsx File/Blob into plain rows.
 *
 * Values come back as trimmed strings — every consumer here is matching codes and names, and a string keeps
 * leading zeros that a numeric coercion would destroy. Fully empty trailing rows are dropped.
 *
 * @param {File|Blob} file
 * @returns {Promise<Object.<string, string[][]>>} sheet name → rows → cells
 */
export const readWorkbook = async (file) => {
    let zip;
    try {
        zip = await JSZip.loadAsync(file);
    } catch {
        throw new Error('The file could not be read as a .xlsx workbook. Re-save it from Excel as "Excel Workbook (.xlsx)".');
    }

    const wbFile = zip.file('xl/workbook.xml');
    if (!wbFile) throw new Error('The file is not an Excel workbook (no xl/workbook.xml). If it is a .xls or .csv, re-save it as .xlsx.');

    // Shared strings are optional — a workbook written entirely with inline strings has none.
    const sharedFile = zip.file('xl/sharedStrings.xml');
    const shared = sharedFile
        ? Array.from(parseXml(await sharedFile.async('string')).getElementsByTagName('si')).map(siText)
        : [];

    // r:id → part path. getAttribute with the namespaced name works across browsers for this fixed prefix.
    const relsDoc = parseXml(await zip.file('xl/_rels/workbook.xml.rels').async('string'));
    const rels = {};
    Array.from(relsDoc.getElementsByTagName('Relationship')).forEach(r => {
        rels[r.getAttribute('Id')] = r.getAttribute('Target').replace(/^\/?xl\//, '').replace(/^\//, '');
    });

    const out = {};
    const sheetEls = Array.from(parseXml(await wbFile.async('string')).getElementsByTagName('sheet'));
    for (const [i, el] of sheetEls.entries()) {
        const name = el.getAttribute('name');
        const rid = el.getAttribute('r:id') || el.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
        const path = `xl/${rels[rid] || `worksheets/sheet${i + 1}.xml`}`;
        const part = zip.file(path);
        if (!part) continue;

        const rows = [];
        for (const rowEl of Array.from(parseXml(await part.async('string')).getElementsByTagName('row'))) {
            // Excel omits empty rows entirely, so honour the r attribute rather than assuming density.
            const rowIdx = parseInt(rowEl.getAttribute('r'), 10) - 1;
            const cells = [];
            for (const c of Array.from(rowEl.getElementsByTagName('c'))) {
                const ref = (c.getAttribute('r') || '').match(/^([A-Z]+)/);
                const idx = ref ? colIndex(ref[1]) : cells.length;
                cells[idx] = String(cellValue(c, shared)).trim();
            }
            rows[rowIdx] = Array.from(cells, v => v ?? '');
        }
        // Fill gaps left by omitted rows, then drop trailing blanks.
        const dense = Array.from(rows, r => r ?? []);
        while (dense.length && dense[dense.length - 1].every(v => !v)) dense.pop();
        out[name] = dense;
    }
    return out;
};
